import { useState, useMemo } from "react";
import {
  Card,
  Button,
  Modal,
  Form,
  Select,
  Input,
  InputNumber,
  Switch,
  Space,
  Tag,
  Popconfirm,
  message,
  Empty,
  Collapse,
  Table,
  TimePicker,
  Checkbox,
  Divider,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined, AppstoreAddOutlined, ImportOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  useGetStaffMemberQuery,
  useGetStaffMembersQuery,
  useCreateStaffScheduleWeekMutation,
  useCreateShiftMutation,
  useUpdateShiftMutation,
  useDeleteShiftMutation,
  useCopyShiftToDaysMutation,
  useCreateBlockMutation,
  useUpdateBlockMutation,
  useDeleteBlockMutation,
  useGetSchedulingMachinesQuery,
  useGetMachineGroupsQuery,
  useGetTasksQuery,
  useGetShiftDayTemplatesQuery,
  useCreateShiftDayTemplateMutation,
  useUpdateShiftDayTemplateMutation,
  useDeleteShiftDayTemplateMutation,
  useApplyDayTemplateMutation,
  useCopyDayTemplatesFromMemberMutation,
  useBulkUpdateShiftMachinesMutation,
} from "../../../redux/api/scheduling";

const DAYS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const DAY_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const DISTRIBUTION_OPTIONS = [
  { value: "front", label: "Front Load" },
  { value: "back", label: "Back Load" },
  { value: "distributed", label: "Distributed" },
];

interface Props {
  memberId: number;
}

export default function StaffTemplateEditor({ memberId }: Props) {
  const { data: member } = useGetStaffMemberQuery(memberId);
  const { data: machinesData } = useGetSchedulingMachinesQuery({});
  const { data: machineGroupsData } = useGetMachineGroupsQuery({});
  const { data: tasksData } = useGetTasksQuery({});
  const [createScheduleWeek] = useCreateStaffScheduleWeekMutation();
  const [createShift] = useCreateShiftMutation();
  const [updateShift] = useUpdateShiftMutation();
  const [deleteShift] = useDeleteShiftMutation();
  const [createBlock] = useCreateBlockMutation();
  const [updateBlock] = useUpdateBlockMutation();
  const [deleteBlock] = useDeleteBlockMutation();
  const [copyShiftToDays] = useCopyShiftToDaysMutation();
  const { data: dayTemplatesData } = useGetShiftDayTemplatesQuery({ staff_member: memberId });
  const [createDayTemplate] = useCreateShiftDayTemplateMutation();
  const [updateDayTemplate] = useUpdateShiftDayTemplateMutation();
  const [deleteDayTemplate] = useDeleteShiftDayTemplateMutation();
  const [applyDayTemplate] = useApplyDayTemplateMutation();
  const [copyFromMember] = useCopyDayTemplatesFromMemberMutation();
  const { data: allStaffData } = useGetStaffMembersQuery({});

  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [editingShift, setEditingShift] = useState<any>(null);
  const [editingWeekId, setEditingWeekId] = useState<number | null>(null);
  const [shiftForm] = Form.useForm();

  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState<any>(null);
  const [editingShiftId, setEditingShiftId] = useState<number | null>(null);
  const [blockForm] = Form.useForm();

  const [dayTemplateModalOpen, setDayTemplateModalOpen] = useState(false);
  const [editingDayTemplate, setEditingDayTemplate] = useState<any>(null);
  const [dayTemplateForm] = Form.useForm();

  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [applyWeekId, setApplyWeekId] = useState<number | null>(null);
  const [applyForm] = Form.useForm();

  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyShiftSource, setCopyShiftSource] = useState<any>(null);
  const [copyForm] = Form.useForm();

  const [copyFromModalOpen, setCopyFromModalOpen] = useState(false);
  const [copyFromForm] = Form.useForm();

  const [bulkUpdateShiftMachines] = useBulkUpdateShiftMachinesMutation();
  const [bulkMachineModalWeekId, setBulkMachineModalWeekId] = useState<number | null>(null);
  const [bulkMachineForm] = Form.useForm();

  const dayTemplates: any[] = dayTemplatesData?.results || dayTemplatesData || [];
  const allStaff: any[] = allStaffData?.results || allStaffData || [];
  const otherStaff = allStaff.filter((s: any) => s.id !== memberId);

  const machines: any[] = machinesData?.results || machinesData || [];
  const machineGroups: any[] = machineGroupsData?.results || machineGroupsData || [];
  const tasks: any[] = tasksData?.results || tasksData || [];
  const weeks = member?.schedule_weeks || [];

  const machineOptions = machines.map((m: any) => ({ value: m.id, label: m.name }));
  const machineGroupOptions = machineGroups.map((g: any) => ({ value: g.id, label: g.name }));
  const taskOptions = tasks.map((t: any) => ({ value: t.id, label: `[${t.task_id}] ${t.name}` }));
  const memberTaskOptions = taskOptions.filter((t: any) =>
    (member?.tasks || []).includes(t.value)
  );

  const machineMap = useMemo(() => {
    const m = new Map<number, string>();
    machines.forEach((mc: any) => m.set(mc.id, mc.name));
    return m;
  }, [machines]);

  const machineGroupMap = useMemo(() => {
    const m = new Map<number, string>();
    machineGroups.forEach((g: any) => m.set(g.id, g.name));
    return m;
  }, [machineGroups]);

  // Build group → member machine IDs map
  const groupMembersMap = useMemo(() => {
    const m = new Map<number, number[]>();
    machineGroups.forEach((g: any) => {
      m.set(g.id, g.machines || []);
    });
    return m;
  }, [machineGroups]);

  // Resolve the actual machine set for a block
  const resolveBlockMachines = (shift: any, block: any): { resolved: string[]; shiftOnly: string[]; extra: string[] } => {
    const shiftMachineIds = new Set<number>();
    // Shift-level machines
    (shift.attended_machines_shift_level || []).forEach((id: number) => shiftMachineIds.add(id));
    // Shift-level groups expanded
    (shift.attended_machine_groups_shift_level || []).forEach((gid: number) => {
      (groupMembersMap.get(gid) || []).forEach((mid: number) => shiftMachineIds.add(mid));
    });

    const blockMachineIds = new Set<number>();
    if (block.use_shift_machines) {
      shiftMachineIds.forEach((id: number) => blockMachineIds.add(id));
    }
    // Block-level machines
    (block.attended_machines || []).forEach((id: number) => blockMachineIds.add(id));
    // Block-level groups expanded
    (block.attended_machine_groups || []).forEach((gid: number) => {
      (groupMembersMap.get(gid) || []).forEach((mid: number) => blockMachineIds.add(mid));
    });

    const resolved = Array.from(blockMachineIds).map((id) => machineMap.get(id) || `#${id}`).sort();
    const shiftOnly = Array.from(shiftMachineIds).map((id) => machineMap.get(id) || `#${id}`).sort();
    const extraIds = Array.from(blockMachineIds).filter((id) => !shiftMachineIds.has(id));
    const extra = extraIds.map((id) => machineMap.get(id) || `#${id}`).sort();

    return { resolved, shiftOnly, extra };
  };

  const renderMachineTags = (groupIds: number[], machineIds: number[]) => (
    <>
      {groupIds.map((gid: number) => (
        <Tag key={`g-${gid}`} color="geekblue">{machineGroupMap.get(gid) || `Group #${gid}`}</Tag>
      ))}
      {machineIds.length > 0 && (
        <Tag color="blue">{machineIds.length} machine(s)</Tag>
      )}
    </>
  );

  const handleAddWeek = async (weekType: "A" | "B") => {
    try {
      await createScheduleWeek({ staffId: memberId, week_type: weekType, staff_member: memberId }).unwrap();
      message.success(`Week ${weekType} template created`);
    } catch (err: any) {
      message.error(err?.data?.detail || "Failed to create week");
    }
  };

  // --- Shift CRUD ---
  const handleOpenShiftCreate = (weekId: number) => {
    setEditingShift(null);
    setEditingWeekId(weekId);
    shiftForm.resetFields();
    shiftForm.setFieldsValue({
      start_day: 1,
      start_time: dayjs("06:00", "HH:mm"),
      end_day: 1,
      end_time: dayjs("14:00", "HH:mm"),
      attended_machines_shift_level: [],
      attended_machine_groups_shift_level: [],
    });
    setShiftModalOpen(true);
  };

  const handleOpenShiftEdit = (shift: any, weekId: number) => {
    setEditingShift(shift);
    setEditingWeekId(weekId);
    shiftForm.setFieldsValue({
      start_day: shift.start_day,
      start_time: shift.start_time ? dayjs(shift.start_time, "HH:mm:ss") : null,
      end_day: shift.end_day,
      end_time: shift.end_time ? dayjs(shift.end_time, "HH:mm:ss") : null,
      attended_machines_shift_level: shift.attended_machines_shift_level || [],
      attended_machine_groups_shift_level: shift.attended_machine_groups_shift_level || [],
    });
    setShiftModalOpen(true);
  };

  const handleSaveShift = async (values: any) => {
    const payload: any = {
      start_day: values.start_day,
      start_time: values.start_time?.format("HH:mm:ss") || "00:00:00",
      end_day: values.end_day,
      end_time: values.end_time?.format("HH:mm:ss") || "23:59:59",
      attended_machines_shift_level: values.attended_machines_shift_level || [],
      attended_machine_groups_shift_level: values.attended_machine_groups_shift_level || [],
    };
    try {
      if (editingShift) {
        await updateShift({ id: editingShift.id, ...payload }).unwrap();
        message.success("Shift updated");
      } else {
        payload.schedule_week = editingWeekId;
        await createShift(payload).unwrap();
        message.success("Shift created");
      }
      setShiftModalOpen(false);
    } catch (err: any) {
      message.error(err?.data?.detail || JSON.stringify(err?.data) || "Error");
    }
  };

  const handleDeleteShift = async (id: number) => {
    await deleteShift(id).unwrap();
    message.success("Shift deleted");
  };

  // --- Block CRUD ---
  const handleOpenBlockCreate = (shiftId: number) => {
    setEditingBlock(null);
    setEditingShiftId(shiftId);
    blockForm.resetFields();
    blockForm.setFieldsValue({
      name: "Block",
      start_day: 1,
      start_time: dayjs("06:00", "HH:mm"),
      end_day: 1,
      end_time: dayjs("08:00", "HH:mm"),
      use_shift_machines: true,
      attended_machines: [],
      attended_machine_groups: [],
      slots: [],
    });
    setBlockModalOpen(true);
  };

  const handleOpenBlockEdit = (block: any, shiftId: number) => {
    setEditingBlock(block);
    setEditingShiftId(shiftId);
    blockForm.setFieldsValue({
      name: block.name || "Block",
      start_day: block.start_day,
      start_time: block.start_time ? dayjs(block.start_time, "HH:mm:ss") : null,
      end_day: block.end_day,
      end_time: block.end_time ? dayjs(block.end_time, "HH:mm:ss") : null,
      use_shift_machines: block.use_shift_machines ?? true,
      attended_machines: block.attended_machines || [],
      attended_machine_groups: block.attended_machine_groups || [],
      slots: (block.slots || []).map((s: any) => ({
        task: s.task,
        count: s.count,
        distribution: s.distribution,
      })),
    });
    setBlockModalOpen(true);
  };

  const handleSaveBlock = async (values: any) => {
    const payload: any = {
      name: values.name || "Block",
      start_day: values.start_day,
      start_time: values.start_time?.format("HH:mm:ss") || "00:00:00",
      end_day: values.end_day,
      end_time: values.end_time?.format("HH:mm:ss") || "23:59:59",
      use_shift_machines: values.use_shift_machines ?? true,
      attended_machines: values.attended_machines || [],
      attended_machine_groups: values.attended_machine_groups || [],
      slots: (values.slots || []).map((s: any) => ({
        task: s.task,
        count: s.count || 1,
        distribution: s.distribution || "distributed",
      })),
    };
    try {
      if (editingBlock) {
        await updateBlock({ id: editingBlock.id, ...payload }).unwrap();
        message.success("Block updated");
      } else {
        payload.shift = editingShiftId;
        await createBlock(payload).unwrap();
        message.success("Block created");
      }
      setBlockModalOpen(false);
    } catch (err: any) {
      message.error(err?.data?.detail || JSON.stringify(err?.data) || "Error");
    }
  };

  const handleDeleteBlock = async (id: number) => {
    await deleteBlock(id).unwrap();
    message.success("Block deleted");
  };

  // --- Day Template CRUD ---
  const handleOpenDayTemplateCreate = () => {
    setEditingDayTemplate(null);
    dayTemplateForm.resetFields();
    dayTemplateForm.setFieldsValue({
      name: "",
      shift_start_time: dayjs("06:00", "HH:mm"),
      shift_end_time: dayjs("14:00", "HH:mm"),
      attended_machines: [],
      attended_machine_groups: [],
      blocks: [],
    });
    setDayTemplateModalOpen(true);
  };

  const handleOpenDayTemplateEdit = (tpl: any) => {
    setEditingDayTemplate(tpl);
    dayTemplateForm.setFieldsValue({
      name: tpl.name,
      shift_start_time: tpl.shift_start_time ? dayjs(tpl.shift_start_time, "HH:mm:ss") : null,
      shift_end_time: tpl.shift_end_time ? dayjs(tpl.shift_end_time, "HH:mm:ss") : null,
      attended_machines: tpl.attended_machines || [],
      attended_machine_groups: tpl.attended_machine_groups || [],
      blocks: (tpl.blocks || []).map((b: any) => ({
        name: b.name || "Block",
        start_time: b.start_time ? dayjs(b.start_time, "HH:mm:ss") : null,
        end_time: b.end_time ? dayjs(b.end_time, "HH:mm:ss") : null,
        use_shift_machines: b.use_shift_machines ?? true,
        attended_machines: b.attended_machines || [],
        attended_machine_groups: b.attended_machine_groups || [],
        slots: (b.slots || []).map((s: any) => ({
          task: s.task,
          count: s.count,
          distribution: s.distribution,
        })),
      })),
    });
    setDayTemplateModalOpen(true);
  };

  const handleSaveDayTemplate = async (values: any) => {
    const payload: any = {
      staff_member: memberId,
      name: values.name,
      shift_start_time: values.shift_start_time?.format("HH:mm:ss") || "06:00:00",
      shift_end_time: values.shift_end_time?.format("HH:mm:ss") || "14:00:00",
      attended_machines: values.attended_machines || [],
      attended_machine_groups: values.attended_machine_groups || [],
      blocks: (values.blocks || []).map((b: any) => ({
        name: b.name || "Block",
        start_time: b.start_time?.format("HH:mm:ss") || "06:00:00",
        end_time: b.end_time?.format("HH:mm:ss") || "08:00:00",
        use_shift_machines: b.use_shift_machines ?? true,
        attended_machines: b.attended_machines || [],
        attended_machine_groups: b.attended_machine_groups || [],
        slots: (b.slots || []).map((s: any) => ({
          task: s.task,
          count: s.count || 1,
          distribution: s.distribution || "distributed",
        })),
      })),
    };
    try {
      if (editingDayTemplate) {
        await updateDayTemplate({ id: editingDayTemplate.id, ...payload }).unwrap();
        message.success("Day template updated");
      } else {
        await createDayTemplate(payload).unwrap();
        message.success("Day template created");
      }
      setDayTemplateModalOpen(false);
    } catch (err: any) {
      message.error(err?.data?.detail || JSON.stringify(err?.data) || "Error saving template");
    }
  };

  const handleDeleteDayTemplate = async (id: number) => {
    await deleteDayTemplate(id).unwrap();
    message.success("Day template deleted");
  };

  // --- Apply Day Template ---
  const handleOpenApply = (weekId: number) => {
    setApplyWeekId(weekId);
    applyForm.resetFields();
    applyForm.setFieldsValue({ template_id: undefined, days: [] });
    setApplyModalOpen(true);
  };

  const handleApplyTemplate = async (values: any) => {
    try {
      await applyDayTemplate({
        id: values.template_id,
        schedule_week_id: applyWeekId,
        days: values.days,
      }).unwrap();
      message.success("Day template applied");
      setApplyModalOpen(false);
    } catch (err: any) {
      message.error(err?.data?.detail || JSON.stringify(err?.data) || "Error applying template");
    }
  };

  // --- Copy Shift to Days ---
  const handleOpenCopy = (shift: any) => {
    setCopyShiftSource(shift);
    copyForm.resetFields();
    copyForm.setFieldsValue({ days: [] });
    setCopyModalOpen(true);
  };

  const handleCopyToDays = async (values: any) => {
    try {
      await copyShiftToDays({ id: copyShiftSource.id, days: values.days }).unwrap();
      message.success("Shift copied to selected days");
      setCopyModalOpen(false);
    } catch (err: any) {
      message.error(err?.data?.detail || JSON.stringify(err?.data) || "Error copying shift");
    }
  };

  // --- Copy Day Templates from Another Staff Member ---
  const handleOpenCopyFrom = () => {
    copyFromForm.resetFields();
    setCopyFromModalOpen(true);
  };

  const handleCopyFromMember = async (values: any) => {
    try {
      await copyFromMember({
        source_member_id: values.source_member_id,
        target_member_id: memberId,
      }).unwrap();
      message.success("Day templates copied from staff member");
      setCopyFromModalOpen(false);
    } catch (err: any) {
      message.error(err?.data?.detail || JSON.stringify(err?.data) || "Error copying templates");
    }
  };

  const hasWeekA = weeks.some((w: any) => w.week_type === "A");
  const hasWeekB = weeks.some((w: any) => w.week_type === "B");

  const dayTemplateColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Shift Time",
      key: "time",
      render: (_: any, r: any) =>
        `${String(r.shift_start_time).slice(0, 5)} – ${String(r.shift_end_time).slice(0, 5)}`,
    },
    {
      title: "Blocks",
      key: "blocks",
      render: (_: any, r: any) => (r.blocks || []).length,
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_: any, r: any) => (
        <Space>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleOpenDayTemplateEdit(r)} />
          <Popconfirm title="Delete day template?" onConfirm={() => handleDeleteDayTemplate(r.id)}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Day Templates section */}
      <Card
        title="Day Templates"
        className="mb-4"
        extra={
          <Space>
            <Button size="small" icon={<ImportOutlined />} onClick={handleOpenCopyFrom} disabled={otherStaff.length === 0}>
              Copy from Staff Member
            </Button>
            <Button size="small" icon={<PlusOutlined />} onClick={handleOpenDayTemplateCreate}>
              New Day Template
            </Button>
          </Space>
        }
      >
        <p className="text-sm text-gray-500 mb-3">
          Reusable shift patterns for this staff member. Apply them to any day in a week template,
          or copy templates from another staff member to get started quickly.
        </p>
        {dayTemplates.length === 0 ? (
          <Empty description="No day templates yet." image={Empty.PRESENTED_IMAGE_SIMPLE}>
            <Button size="small" onClick={handleOpenDayTemplateCreate}>Create first template</Button>
          </Empty>
        ) : (
          <Table
            dataSource={dayTemplates}
            columns={dayTemplateColumns}
            rowKey="id"
            size="small"
            pagination={false}
          />
        )}
      </Card>

      <Card
        title="Schedule Templates"
        className="mb-4"
        extra={
          <Space>
            {!hasWeekA && (
              <Button size="small" onClick={() => handleAddWeek("A")}>
                Add Week A
              </Button>
            )}
            {!hasWeekB && (
              <Button size="small" onClick={() => handleAddWeek("B")}>
                Add Week B
              </Button>
            )}
          </Space>
        }
      >
        <p className="text-sm text-gray-500 mb-3">
          Define reusable weekly templates. Deploy them to specific calendar weeks
          in the Work Schedule tab.
        </p>
        {weeks.length === 0 ? (
          <Empty description="No schedule templates. Add Week A to start." />
        ) : (
          weeks.map((week: any) => (
            <Card
              key={week.id}
              title={`Week ${week.week_type} Template`}
              className="mb-4"
              extra={
                <Space>
                  <Button
                    size="small"
                    onClick={() => {
                      setBulkMachineModalWeekId(week.id);
                      bulkMachineForm.resetFields();
                    }}
                  >
                    Set Machines (All Shifts)
                  </Button>
                  <Button
                    size="small"
                    icon={<AppstoreAddOutlined />}
                    onClick={() => handleOpenApply(week.id)}
                  >
                    Apply Template
                  </Button>
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => handleOpenShiftCreate(week.id)}
                  >
                    Add Shift
                  </Button>
                </Space>
              }
            >
              {(week.shifts || []).length === 0 ? (
                <Empty description="No shifts" image={Empty.PRESENTED_IMAGE_SIMPLE}>
                  <Button size="small" onClick={() => handleOpenShiftCreate(week.id)}>
                    Add first shift
                  </Button>
                </Empty>
              ) : (
                <Collapse>
                  {week.shifts.map((shift: any) => (
                    <Collapse.Panel
                      key={shift.id}
                      header={
                        <Space size={[4, 4]} wrap>
                          <span>
                            {DAY_LABELS[shift.start_day]} {String(shift.start_time).slice(0, 5)} –{" "}
                            {DAY_LABELS[shift.end_day]} {String(shift.end_time).slice(0, 5)}
                          </span>
                          {renderMachineTags(
                            shift.attended_machine_groups_shift_level || [],
                            shift.attended_machines_shift_level || [],
                          )}
                        </Space>
                      }
                      extra={
                        <Space onClick={(e) => e.stopPropagation()}>
                          <Button
                            type="text"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => handleOpenBlockCreate(shift.id)}
                          >
                            Block
                          </Button>
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => handleOpenCopy(shift)}
                          >
                            Copy
                          </Button>
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleOpenShiftEdit(shift, week.id)}
                          />
                          <Popconfirm
                            title="Delete shift?"
                            onConfirm={() => handleDeleteShift(shift.id)}
                          >
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </Space>
                      }
                    >
                      {(shift.blocks || []).length === 0 ? (
                        <Empty description="No blocks" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                      ) : (
                        shift.blocks.map((block: any) => (
                          <Card
                            key={block.id}
                            size="small"
                            className="mb-2"
                            title={
                              <Space size={[4, 4]} wrap>
                                <span>{block.name}</span>
                                <span className="text-xs text-gray-400">
                                  {DAY_LABELS[block.start_day]} {String(block.start_time).slice(0, 5)} –{" "}
                                  {DAY_LABELS[block.end_day]} {String(block.end_time).slice(0, 5)}
                                </span>
                                {(() => {
                                  const { resolved, extra } = resolveBlockMachines(shift, block);
                                  return (
                                    <>
                                      {resolved.map((name: string) => (
                                        <Tag key={name} color={extra.includes(name) ? "orange" : "blue"} style={{ fontSize: 10, margin: "1px 2px" }}>{name}</Tag>
                                      ))}
                                      {block.use_shift_machines && extra.length > 0 && (
                                        <Tag color="warning" style={{ fontSize: 10 }}>+{extra.length} beyond shift</Tag>
                                      )}
                                    </>
                                  );
                                })()}
                              </Space>
                            }
                            extra={
                              <Space>
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => handleOpenBlockEdit(block, shift.id)}
                                />
                                <Popconfirm
                                  title="Delete block?"
                                  onConfirm={() => handleDeleteBlock(block.id)}
                                >
                                  <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                              </Space>
                            }
                          >
                            {(block.slots || []).length > 0 ? (
                              <Table
                                dataSource={block.slots}
                                rowKey="id"
                                size="small"
                                pagination={false}
                                columns={[
                                  { title: "Task", dataIndex: "task_name", key: "task_name" },
                                  { title: "ID", dataIndex: "task_task_id", key: "task_task_id", width: 60 },
                                  { title: "Slots", dataIndex: "count", key: "count", width: 60 },
                                  {
                                    title: "Distribution",
                                    dataIndex: "distribution",
                                    key: "distribution",
                                    render: (v: string) => (
                                      <Tag color={v === "front" ? "blue" : v === "back" ? "orange" : "green"}>
                                        {v === "front" ? "Front" : v === "back" ? "Back" : "Distributed"}
                                      </Tag>
                                    ),
                                  },
                                ]}
                              />
                            ) : (
                              <p className="text-gray-400 italic text-xs">No task slots</p>
                            )}
                          </Card>
                        ))
                      )}
                    </Collapse.Panel>
                  ))}
                </Collapse>
              )}
            </Card>
          ))
        )}
      </Card>

      {/* Shift Modal */}
      <Modal
        title={editingShift ? "Edit Shift" : "Add Shift"}
        open={shiftModalOpen}
        onCancel={() => setShiftModalOpen(false)}
        onOk={() => shiftForm.submit()}
        width={520}
        destroyOnClose
      >
        <Form form={shiftForm} layout="vertical" onFinish={handleSaveShift}>
          <div className="grid grid-cols-2 gap-2">
            <Form.Item name="start_day" label="Start Day" rules={[{ required: true }]}>
              <Select options={DAYS} />
            </Form.Item>
            <Form.Item name="start_time" label="Start Time" rules={[{ required: true }]}>
              <TimePicker format="HH:mm" className="w-full" />
            </Form.Item>
            <Form.Item name="end_day" label="End Day" rules={[{ required: true }]}>
              <Select options={DAYS} />
            </Form.Item>
            <Form.Item name="end_time" label="End Time" rules={[{ required: true }]}>
              <TimePicker format="HH:mm" className="w-full" />
            </Form.Item>
          </div>
          <Form.Item
            name="attended_machines_shift_level"
            label="Attended Machines (shift level)"
            tooltip="Individual machines this staff member attends during the entire shift."
          >
            <Select mode="multiple" options={machineOptions} placeholder="Select machines..." />
          </Form.Item>
          <Form.Item
            name="attended_machine_groups_shift_level"
            label="Attended Machine Groups (shift level)"
            tooltip="Machine groups attended during the entire shift. All machines in these groups are attended."
          >
            <Select mode="multiple" options={machineGroupOptions} placeholder="Select machine groups..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Block Modal */}
      <Modal
        title={editingBlock ? "Edit Block" : "Add Block"}
        open={blockModalOpen}
        onCancel={() => setBlockModalOpen(false)}
        onOk={() => blockForm.submit()}
        width={650}
        destroyOnClose
      >
        <Form form={blockForm} layout="vertical" onFinish={handleSaveBlock}>
          <Form.Item name="name" label="Block Name">
            <Input placeholder="e.g. Check-In Block, Run Block" />
          </Form.Item>
          <div className="grid grid-cols-2 gap-2">
            <Form.Item name="start_day" label="Start Day" rules={[{ required: true }]}>
              <Select options={DAYS} />
            </Form.Item>
            <Form.Item name="start_time" label="Start Time" rules={[{ required: true }]}>
              <TimePicker format="HH:mm" className="w-full" />
            </Form.Item>
            <Form.Item name="end_day" label="End Day" rules={[{ required: true }]}>
              <Select options={DAYS} />
            </Form.Item>
            <Form.Item name="end_time" label="End Time" rules={[{ required: true }]}>
              <TimePicker format="HH:mm" className="w-full" />
            </Form.Item>
          </div>
          <Form.Item
            name="use_shift_machines"
            label="Use Shift-Level Machines"
            valuePropName="checked"
            tooltip="If on, this block inherits the shift's machine list"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="attended_machines"
            label="Block-Level Attended Machines"
            tooltip="Override or supplement shift machines for this block"
          >
            <Select mode="multiple" options={machineOptions} placeholder="Select machines..." />
          </Form.Item>
          <Form.Item
            name="attended_machine_groups"
            label="Block-Level Attended Machine Groups"
            tooltip="Machine groups attended during this block"
          >
            <Select mode="multiple" options={machineGroupOptions} placeholder="Select machine groups..." />
          </Form.Item>

          <Card size="small" title="Task Slots" className="mb-2">
            <Form.List name="slots">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} className="flex gap-2 items-end mb-2">
                      <Form.Item {...restField} name={[name, "task"]} label="Task" className="flex-1 mb-0">
                        <Select options={memberTaskOptions} placeholder="Task" showSearch filterOption={(input, opt) => (opt?.label?.toString() || "").toLowerCase().includes(input.toLowerCase())} />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, "count"]} label="Slots" className="w-20 mb-0">
                        <InputNumber min={1} />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, "distribution"]} label="Dist." className="w-32 mb-0">
                        <Select options={DISTRIBUTION_OPTIONS} />
                      </Form.Item>
                      <Button danger onClick={() => remove(name)} icon={<DeleteOutlined />} className="mb-0" />
                    </div>
                  ))}
                  <Button type="dashed" onClick={() => add({ count: 1, distribution: "distributed" })} icon={<PlusOutlined />} block>
                    Add Slot
                  </Button>
                </>
              )}
            </Form.List>
          </Card>
        </Form>
      </Modal>

      {/* Day Template Modal */}
      <Modal
        title={editingDayTemplate ? "Edit Day Template" : "New Day Template"}
        open={dayTemplateModalOpen}
        onCancel={() => setDayTemplateModalOpen(false)}
        onOk={() => dayTemplateForm.submit()}
        width={700}
        destroyOnClose
      >
        <Form form={dayTemplateForm} layout="vertical" onFinish={handleSaveDayTemplate}>
          <Form.Item name="name" label="Template Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Standard Day Shift, Night Shift" />
          </Form.Item>
          <div className="grid grid-cols-2 gap-2">
            <Form.Item name="shift_start_time" label="Shift Start" rules={[{ required: true }]}>
              <TimePicker format="HH:mm" className="w-full" />
            </Form.Item>
            <Form.Item name="shift_end_time" label="Shift End" rules={[{ required: true }]}>
              <TimePicker format="HH:mm" className="w-full" />
            </Form.Item>
          </div>
          <Form.Item name="attended_machines" label="Attended Machines">
            <Select mode="multiple" options={machineOptions} placeholder="Select machines..." />
          </Form.Item>
          <Form.Item name="attended_machine_groups" label="Attended Machine Groups">
            <Select mode="multiple" options={machineGroupOptions} placeholder="Select machine groups..." />
          </Form.Item>

          <Divider orientation="left">Blocks</Divider>
          <Form.List name="blocks">
            {(blockFields, { add: addBlock, remove: removeBlock }) => (
              <>
                {blockFields.map(({ key: bKey, name: bName, ...bRest }) => (
                  <Card
                    key={bKey}
                    size="small"
                    className="mb-3"
                    title={`Block ${bName + 1}`}
                    extra={<Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeBlock(bName)} />}
                  >
                    <Form.Item {...bRest} name={[bName, "name"]} label="Name">
                      <Input placeholder="Block name" />
                    </Form.Item>
                    <div className="grid grid-cols-2 gap-2">
                      <Form.Item {...bRest} name={[bName, "start_time"]} label="Start" rules={[{ required: true }]}>
                        <TimePicker format="HH:mm" className="w-full" />
                      </Form.Item>
                      <Form.Item {...bRest} name={[bName, "end_time"]} label="End" rules={[{ required: true }]}>
                        <TimePicker format="HH:mm" className="w-full" />
                      </Form.Item>
                    </div>
                    <Form.Item {...bRest} name={[bName, "use_shift_machines"]} label="Use Shift Machines" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item {...bRest} name={[bName, "attended_machines"]} label="Block Machines">
                      <Select mode="multiple" options={machineOptions} placeholder="Select machines..." />
                    </Form.Item>
                    <Form.Item {...bRest} name={[bName, "attended_machine_groups"]} label="Block Machine Groups">
                      <Select mode="multiple" options={machineGroupOptions} placeholder="Select groups..." />
                    </Form.Item>
                    <Card size="small" title="Task Slots" className="mb-1">
                      <Form.List name={[bName, "slots"]}>
                        {(slotFields, { add: addSlot, remove: removeSlot }) => (
                          <>
                            {slotFields.map(({ key: sKey, name: sName, ...sRest }) => (
                              <div key={sKey} className="flex gap-2 items-end mb-2">
                                <Form.Item {...sRest} name={[sName, "task"]} label="Task" className="flex-1 mb-0">
                                  <Select options={memberTaskOptions} placeholder="Task" showSearch filterOption={(input, opt) => (opt?.label?.toString() || "").toLowerCase().includes(input.toLowerCase())} />
                                </Form.Item>
                                <Form.Item {...sRest} name={[sName, "count"]} label="Slots" className="w-20 mb-0">
                                  <InputNumber min={1} />
                                </Form.Item>
                                <Form.Item {...sRest} name={[sName, "distribution"]} label="Dist." className="w-32 mb-0">
                                  <Select options={DISTRIBUTION_OPTIONS} />
                                </Form.Item>
                                <Button danger onClick={() => removeSlot(sName)} icon={<DeleteOutlined />} className="mb-0" />
                              </div>
                            ))}
                            <Button type="dashed" onClick={() => addSlot({ count: 1, distribution: "distributed" })} icon={<PlusOutlined />} block>
                              Add Slot
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </Card>
                  </Card>
                ))}
                <Button type="dashed" onClick={() => addBlock({ name: "Block", use_shift_machines: true, slots: [] })} icon={<PlusOutlined />} block>
                  Add Block
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* Apply Day Template Modal */}
      <Modal
        title="Apply Day Template"
        open={applyModalOpen}
        onCancel={() => setApplyModalOpen(false)}
        onOk={() => applyForm.submit()}
        width={420}
        destroyOnClose
      >
        <Form form={applyForm} layout="vertical" onFinish={handleApplyTemplate}>
          <Form.Item name="template_id" label="Day Template" rules={[{ required: true, message: "Select a template" }]}>
            <Select
              placeholder="Select a day template..."
              options={dayTemplates.map((t: any) => ({
                value: t.id,
                label: `${t.name} (${String(t.shift_start_time).slice(0, 5)}–${String(t.shift_end_time).slice(0, 5)})`,
              }))}
            />
          </Form.Item>
          <Form.Item name="days" label="Apply to Days" rules={[{ required: true, message: "Select at least one day" }]}>
            <Checkbox.Group
              options={DAYS.map((d) => ({ label: d.label, value: d.value }))}
              className="flex flex-col gap-1"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Copy Shift to Days Modal */}
      <Modal
        title={`Copy Shift to Other Days`}
        open={copyModalOpen}
        onCancel={() => setCopyModalOpen(false)}
        onOk={() => copyForm.submit()}
        width={420}
        destroyOnClose
      >
        <p className="text-sm text-gray-500 mb-3">
          This will duplicate the selected shift (with all blocks and slots) to the chosen days.
        </p>
        <Form form={copyForm} layout="vertical" onFinish={handleCopyToDays}>
          <Form.Item name="days" label="Copy to Days" rules={[{ required: true, message: "Select at least one day" }]}>
            <Checkbox.Group
              options={DAYS.map((d) => ({ label: d.label, value: d.value }))}
              className="flex flex-col gap-1"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Copy Day Templates from Another Staff Member */}
      <Modal
        title="Copy Day Templates from Staff Member"
        open={copyFromModalOpen}
        onCancel={() => setCopyFromModalOpen(false)}
        onOk={() => copyFromForm.submit()}
        width={420}
        destroyOnClose
      >
        <p className="text-sm text-gray-500 mb-3">
          This will copy all day templates from the selected staff member into this member's templates.
          The copies are independent and can be edited afterwards.
        </p>
        <Form form={copyFromForm} layout="vertical" onFinish={handleCopyFromMember}>
          <Form.Item name="source_member_id" label="Copy from" rules={[{ required: true, message: "Select a staff member" }]}>
            <Select
              placeholder="Select a staff member..."
              showSearch
              filterOption={(input, opt) => (opt?.label?.toString() || "").toLowerCase().includes(input.toLowerCase())}
              options={otherStaff.map((s: any) => ({
                value: s.id,
                label: s.name,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Bulk Update Shift Machines */}
      <Modal
        title="Set Machines for All Shifts"
        open={bulkMachineModalWeekId !== null}
        onCancel={() => setBulkMachineModalWeekId(null)}
        onOk={() => bulkMachineForm.submit()}
        okText="Apply to All Shifts"
      >
        <p className="text-sm text-gray-500 mb-4">
          This will set the selected machines and groups on <strong>every shift</strong> in this week template, replacing their current machine assignments.
        </p>
        <Form
          form={bulkMachineForm}
          layout="vertical"
          onFinish={async (values) => {
            if (!bulkMachineModalWeekId) return;
            try {
              await bulkUpdateShiftMachines({
                id: bulkMachineModalWeekId,
                attended_machines_shift_level: values.attended_machines_shift_level || [],
                attended_machine_groups_shift_level: values.attended_machine_groups_shift_level || [],
              }).unwrap();
              message.success("Machines updated on all shifts");
              setBulkMachineModalWeekId(null);
            } catch (err: any) {
              message.error(err?.data?.detail || "Error updating machines");
            }
          }}
        >
          <Form.Item name="attended_machines_shift_level" label="Machines">
            <Select
              mode="multiple"
              placeholder="Select machines..."
              options={machineOptions}
              showSearch
              filterOption={(input, opt) =>
                (opt?.label?.toString() || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="attended_machine_groups_shift_level" label="Machine Groups">
            <Select
              mode="multiple"
              placeholder="Select machine groups..."
              options={machineGroupOptions}
              showSearch
              filterOption={(input, opt) =>
                (opt?.label?.toString() || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
